import { createFileRoute, redirect, useSearch } from '@tanstack/react-router'
import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { CompanySidebar, type CompanySection } from '@/components/settings/company-sidebar'
import { useCurrentOrganization } from '@/stores/organization.store'
import { SkeletonBar } from '@/components/shared/skeleton-bar'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { GeneralSection } from '@/components/settings/organization/general-section'
import { TimeSettingsSection } from '@/components/settings/organization/time-settings-section'
import { BillingSection } from '@/components/settings/organization/billing-section'
import { LeadAssignmentSection } from '@/components/settings/organization/lead-assignment-section'
import { LeadReferralsSection } from '@/components/settings/organization/lead-referrals-section'
import { useHasLeadReferralsFeature } from '@/hooks/use-has-feature'
import { requirePermission, PermissionDeniedRoute } from '@/lib/require-permission-guard'
import {
  buildCompanySettingsSearch,
  parseBillingCheckoutSearch,
} from '@/lib/billing-checkout-search'

type CompanySearch = {
  section?: CompanySection
  // Forwarded from the Stripe Billing checkout success_url ({CHECKOUT_ID} substituted
  // server-side, §3.9) so BillingSection can resume the post-checkout
  // invalidate+poll flow if a redirect-based payment method lands here
  // instead of completing inside the embedded checkout iframe (F10).
  checkout_id?: string
}

export const Route = createFileRoute('/_app/_settings/company')({
  component: CompanyPage,
  validateSearch: (search: Record<string, unknown>): CompanySearch => {
    const billingCheckoutSearch = parseBillingCheckoutSearch(search)
    return {
      section: (billingCheckoutSearch.section as CompanySection) || 'general',
      checkout_id: billingCheckoutSearch.checkout_id,
    }
  },
  // Company bundles two domains (general/time-settings/lead-assignment/lead-referrals ->
  // SettingsManage, subscription -> BillingManage per decision D2). Either
  // permission opens the whole page: the sections themselves are not
  // individually gated, so a BillingManage-only user still sees the profile
  // sections in the rail.
  beforeLoad: async ({ context, search }) => {
    // Terms and public domains moved to the Client Portal page; Billing was
    // renamed to Subscription. The checkout_id is carried across the Billing
    // hop so a redirect-based payment method can still resume its poll (F10).
    const rawSection = search.section as string | undefined
    if (rawSection === 'terms') {
      throw redirect({ to: '/client-portal', search: { section: 'terms' } })
    }
    if (rawSection === 'public-domains') {
      throw redirect({ to: '/client-portal', search: { section: 'domain' } })
    }
    if (rawSection === 'billing') {
      throw redirect({
        to: '/company',
        search: buildCompanySettingsSearch(
          'subscription' as CompanySection,
          search.checkout_id as string | undefined
        ),
      })
    }
    await requirePermission(
      context.queryClient,
      ['SettingsManage', 'BillingManage'],
      'company settings'
    )
  },
  errorComponent: PermissionDeniedRoute,
})

function CompanyPage() {
  useBreadcrumbFromRoute()
  const search = useSearch({ from: '/_app/_settings/company' })
  const { currentOrganization, isLoading, refetch } = useCurrentOrganization()
  const { hasFeature: hasLeadReferrals } = useHasLeadReferralsFeature()
  // Without the feature, a lead-referrals link lands on General.
  const currentSection =
    search.section === 'lead-referrals' && !hasLeadReferrals ? 'general' : search.section || 'general'

  return (
    <div className="flex h-full overflow-hidden">
      <div className="scroll-fade-y flex-shrink-0 overflow-y-auto">
        <CompanySidebar />
      </div>
      <div className="flex flex-1 flex-col overflow-y-auto">
        <div className="flex flex-col gap-4 p-6">
          {isLoading ? (
            <LoadingSkeleton />
          ) : currentOrganization ? (
            <>
              {currentSection === 'general' && (
                <GeneralSection organization={currentOrganization} onRefetch={refetch} />
              )}
              {currentSection === 'time-settings' && (
                <TimeSettingsSection organization={currentOrganization} onRefetch={refetch} />
              )}
              {currentSection === 'subscription' && (
                <BillingSection checkoutId={search.checkout_id} />
              )}
              {currentSection === 'lead-assignment' && (
                <LeadAssignmentSection organization={currentOrganization} onRefetch={refetch} />
              )}
              {currentSection === 'lead-referrals' && (
                <LeadReferralsSection organization={currentOrganization} />
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-muted-foreground">No organization selected</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div>
        <SkeletonBar className="h-8 w-64 mb-2" />
        <SkeletonBar className="h-4 w-96" />
      </div>
      <Card>
        <CardHeader>
          <SkeletonBar className="h-6 w-48" />
          <SkeletonBar className="h-4 w-72" />
        </CardHeader>
        <CardContent className="space-y-4">
          <SkeletonBar className="h-10 w-full" />
          <SkeletonBar className="h-10 w-full" />
          <SkeletonBar className="h-10 w-full" />
        </CardContent>
      </Card>
    </div>
  )
}
